import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios';
import notificationSound from '../utils/notificationSound';
import getImageUrl from '../utils/imageUrl';

export default function BookingNotificationToast() {
  const [notifications, setNotifications] = useState([]);
  const [lastNotificationId, setLastNotificationId] = useState(null);

  const checkForNewBookings = async () => {
    try {
      const res = await api.get('/notifications');
      const notificationsData = res.data?.data || res.data || [];
      // Filter booking notifications for admin only (user_id = null)
      const bookingNotifications = notificationsData.filter(n => 
        n.type === 'booking' && !n.is_read && n.user_id === null
      );

      if (bookingNotifications.length > 0) {
        const latestNotification = bookingNotifications[0];
        
        // Show toast only for new notifications
        if (latestNotification.id !== lastNotificationId) {
          setNotifications([latestNotification]);
          setLastNotificationId(latestNotification.id);
          
          // Play notification sound
          notificationSound.playBookingNotification();
          
          // Auto hide after 8 seconds for more data
          setTimeout(() => {
            setNotifications([]);
          }, 8000);
        }
      }
    } catch (error) {
      // Silently fail - backend might not be ready
    }
  };

  useEffect(() => {
    const interval = setInterval(checkForNewBookings, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [lastNotificationId]);

  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    // Mark as read
    api.put(`/notifications/${id}`, { is_read: true }).catch(console.error);
  };

  return (
    <div className="fixed top-4 right-2 sm:right-4 z-[9999] space-y-2 max-w-[calc(100vw-1rem)] sm:max-w-sm">
      <AnimatePresence>
        {notifications.map((notification) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, x: 300, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 300, scale: 0.8 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 30 
            }}
            className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-4 sm:p-5 w-full overflow-hidden"
          >
            {/* Blue header bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-blue-600"></div>
            
            <div className="flex items-start">
              <motion.div 
                className="flex-shrink-0 mr-4"
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  duration: 0.5,
                  repeat: 3
                }}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg overflow-hidden">
                  {notification.data?.property_image && notification.data.property_image !== 'null' ? (
                    <img 
                      src={getImageUrl(notification.data.property_image)} 
                      alt={notification.data.property_name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-full flex items-center justify-center ${notification.data?.property_image && notification.data.property_image !== 'null' ? 'hidden' : 'flex'}`}
                  >
                    <svg 
                      className="w-4 h-4 sm:w-6 sm:h-6 text-white" 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                </div>
              </motion.div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                    <p className="text-sm sm:text-base font-bold text-gray-900">
                      Booking Baru!
                    </p>
                  </div>
                  <motion.button
                    onClick={() => dismissNotification(notification.id)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </motion.button>
                </div>
                
                {notification.data ? (
                  <div className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-4 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-xs">
                      <p className="truncate">📅 {notification.data.start_date || 'N/A'}</p>
                      <p className="truncate">📅 {notification.data.end_date || 'N/A'}</p>
                      <p className="truncate">💰 Rp {notification.data.total_amount ? new Intl.NumberFormat('id-ID').format(notification.data.total_amount) : '0'}</p>
                      {notification.data.guests && <p>👥 {notification.data.guests} tamu</p>}
                    </div>
                    <p className="text-xs text-gray-500 truncate">👤 {notification.data.user_name || 'Unknown'}</p>
                  </div>
                ) : (
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                    {notification.message}
                  </p>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-xs text-gray-500">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
                    Baru saja
                  </div>
                  <motion.button
                    onClick={() => dismissNotification(notification.id)}
                    className="text-xs bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 sm:px-4 py-2 rounded-full hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Lihat Detail
                  </motion.button>
                </div>
              </div>
            </div>
            
            {/* Progress bar */}
            <motion.div 
              className="mt-4 h-1.5 bg-gray-200 rounded-full overflow-hidden"
              initial={{ width: "100%" }}
            >
              <motion.div 
                className="h-full bg-gradient-to-r from-blue-400 to-blue-600"
                initial={{ width: "100%" }}
                animate={{ width: "0%" }}
                transition={{ duration: 8, ease: "linear" }}
              />
            </motion.div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}